// "Take payment" — a client calls in to pay an invoice for COMPLETED work.
// The card goes from this browser straight to Authorize.net (Accept.js); the
// Hub only receives a one-time token, never the card number. Or send the
// client a secure pay link instead.
import { useEffect, useState } from "react";
import { Modal } from "../Modal";
import { ApiError, payments as paymentsApi, type PaymentsOverview } from "../../lib/api";
import { tokenizeCard, useAcceptJsLoader } from "../../lib/acceptjs";

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function TakePaymentModal({
  clientId, overview, onClose, onSaved, onRefresh,
}: {
  clientId: string;
  overview: Pick<PaymentsOverview, "open_invoices">;
  onClose: () => void;
  onSaved: (message: string) => void;
  /** Re-fetch balances (the parent passes fresh `overview` back in). */
  onRefresh?: () => void | Promise<void>;
}) {
  // CROA: only invoices for work that's already done can be charged.
  const chargeable = overview.open_invoices.filter((i) => i.work_completed && i.balance_cents > 0 && i.status !== "void");
  const blocked = overview.open_invoices.filter((i) => !i.work_completed && i.balance_cents > 0 && i.status !== "void");
  const [invoiceId, setInvoiceId] = useState(chargeable[0]?.id || "");
  const invoice = chargeable.find((i) => i.id === invoiceId);
  const [cfg, setCfg] = useState<{ available: boolean; mode: string; client_key: string | null; api_login_id: string | null } | null>(null);
  const [card, setCard] = useState({ number: "", exp: "", cvc: "", zip: "" });
  const [sendReceipt, setSendReceipt] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [linkMsg, setLinkMsg] = useState<string | null>(null);

  useEffect(() => {
    paymentsApi.acceptConfig().then(setCfg).catch(() => setCfg({ available: false, mode: "", client_key: null, api_login_id: null }));
  }, []);
  const usable = !!(cfg?.available && cfg.client_key && cfg.api_login_id);
  const { ready, failed } = useAcceptJsLoader(cfg?.mode, usable && !!invoice);

  async function charge() {
    setErr(null);
    if (!invoice || !cfg?.client_key || !cfg.api_login_id) return;
    const digits = card.number.replace(/\D/g, "");
    const m = /^(\d{1,2})\s*\/\s*(\d{2}|\d{4})$/.exec(card.exp.trim());
    if (digits.length < 13) return setErr("Enter the full card number.");
    if (!m) return setErr("Enter the expiration as MM/YY.");
    if (!/^\d{3,4}$/.test(card.cvc.trim())) return setErr("Enter the 3- or 4-digit security code.");
    setBusy(true);
    try {
      const opaque = await tokenizeCard(
        { clientKey: cfg.client_key, apiLoginID: cfg.api_login_id },
        { cardNumber: digits, month: m[1].padStart(2, "0"), year: m[2].length === 2 ? `20${m[2]}` : m[2], cardCode: card.cvc.trim(), zip: card.zip.trim() || undefined },
      );
      setCard({ number: "", exp: "", cvc: "", zip: "" }); // never keep card details around
      // Charge exactly the amount on the button; the server refuses if it changed.
      const r = await paymentsApi.take(clientId, invoice.id, opaque, invoice.balance_cents, sendReceipt);
      const bits = [`${money(r.payment.amount_cents)} charged.`, r.invoice?.status === "paid" ? "Invoice marked Paid." : ""];
      if (sendReceipt) bits.push(r.receipt_sent ? "Receipt emailed." : `Receipt not sent${r.receipt_error ? ` (${r.receipt_error})` : ""}.`);
      onSaved(bits.filter(Boolean).join(" "));
    } catch (e: any) {
      const code = e instanceof ApiError ? e.body?.error : null;
      if (code === "amount_changed") await onRefresh?.(); // pull the current balance into this form
      setErr(code === "amount_changed"
        ? `The balance on this invoice changed${e.body?.balance_cents != null ? ` to ${money(e.body.balance_cents)}` : ""}, so nothing was charged. The amount on the button is now up to date — re-enter the card to charge it.`
        : code === "work_not_completed"
        ? "This invoice is for work that isn't completed yet, so it can't be charged."
        : e?.body?.message || e?.message || "The card couldn't be charged.");
    } finally { setBusy(false); }
  }

  async function sendLink(channel: "email" | "sms") {
    if (!invoice) return;
    setErr(null); setLinkMsg(null); setBusy(true);
    try {
      await paymentsApi.sendPayLink(invoice.id, channel);
      setLinkMsg(channel === "email" ? "Pay link emailed to the client." : "Pay link texted to the client.");
    } catch (e: any) {
      setErr(e?.body?.error === "sms_unavailable" ? "Texting isn't set up for your account yet — email the link instead." : e?.body?.message || e?.message || "Couldn't send the link.");
    } finally { setBusy(false); }
  }

  return (
    <Modal open title="Take payment" onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
          {usable && invoice && (
            <button type="button" className="primary-button" onClick={charge} disabled={busy || !ready} data-testid="take-payment-charge">
              {busy ? "Charging…" : `Charge ${money(invoice.balance_cents)}`}
            </button>
          )}
        </>
      }>
      {!chargeable.length ? (
        <p className="muted" style={{ margin: 0 }}>
          There's no invoice for completed work to charge.{blocked.length ? " This client's open invoices are for work that isn't done yet — under CROA they can't be charged until it is." : " Create an invoice for the completed work on the Billing page first."}
        </p>
      ) : (
        <div className="stacked-form">
          <label className="form-field"><span>Invoice (completed work)</span>
            <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
              {chargeable.map((i) => <option key={i.id} value={i.id}>{(i.description || `Invoice ${i.id.slice(0, 8)}`).slice(0, 60)} — {money(i.balance_cents)}</option>)}
            </select>
          </label>
          {cfg && !usable && (
            <p className="banner-warning" style={{ margin: 0 }}>Card payments aren't set up yet. Add your Authorize.net keys (including the Public Client Key) under Billing → Payment processors. You can still send a pay link.</p>
          )}
          {usable && (
            <>
              {failed && <p className="error-text" role="alert" style={{ margin: 0 }}>The secure card form couldn't load. Check the connection and try again.</p>}
              <label className="form-field"><span>Card number</span>
                <input inputMode="numeric" autoComplete="off" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} placeholder="•••• •••• •••• ••••" />
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <label className="form-field"><span>Expires</span>
                  <input autoComplete="off" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} placeholder="MM/YY" /></label>
                <label className="form-field"><span>Security code</span>
                  <input inputMode="numeric" autoComplete="off" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} placeholder="CVC" /></label>
                <label className="form-field"><span>ZIP</span>
                  <input inputMode="numeric" autoComplete="off" value={card.zip} onChange={(e) => setCard({ ...card, zip: e.target.value })} /></label>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} />
                <span>Email the client a receipt</span>
              </label>
              <p className="muted small" style={{ margin: 0 }}>The card goes straight to Authorize.net. The Hub never stores card numbers.</p>
            </>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span className="muted small">Or let them pay on their own:</span>
            <button type="button" className="btn-secondary" onClick={() => sendLink("email")} disabled={busy}>Email pay link</button>
            <button type="button" className="btn-secondary" onClick={() => sendLink("sms")} disabled={busy}>Text pay link</button>
          </div>
          {linkMsg && <p className="small" role="status" style={{ margin: 0, color: "var(--ne-success-text)" }}>{linkMsg}</p>}
          {err && <p className="error-text" role="alert" style={{ margin: 0 }}>{err}</p>}
        </div>
      )}
    </Modal>
  );
}
